import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "react-oidc-context";
import { createPortal } from "react-dom";
import _ from "lodash";
import { Button, CircularProgress } from "@mui/material";
import { icons } from "../../../svg";
import { useNitroStore } from "../../../store/Store";
import "./mobileSplitFavoriteOriginPanel.css";
import { useMutateFavoriteOrigins } from "../../../hooks/useMutateFavoriteOrigins";
import NitroService from "../../../utils/nitro.service";
import { mapFavoritesByMarketerToLegacy } from "../../../utils/favoriteOriginsMapper";
import {
  createFavorite as createFavoriteCrud,
  deleteFavorite as deleteFavoriteCrud,
  persistPeriodPrice,
  toggleSplitMonth,
} from "../../../pages/home/favoriteOrigin/originManagerCrudActions";
import AddOriginColumn, {
  SelectedOrigin,
} from "../../../pages/home/favoriteOrigin/AddOriginColumn";
import {
  ICreatePeriodPriceResponse,
  IFavoriteOrigin,
  IFavoriteOriginMonth,
  IFavoriteOriginPeriod,
  IFavoriteOriginPeriodPrice,
  IFavoriteOriginPricesResponse,
  IFavoriteOriginWithMonths,
  IMonthSplitResponse,
  OriginDisplayInfo,
  PriceField,
} from "../../../types/IFavoriteOrigins";
import ILocation from "../../../types/ILocation";

import GuidancePanel from "../../guidancePanel";
import IndicationHistoryPanel from "../../../pages/home/favoriteOrigin/IndicationHistoryPanel";
import InsightsPanel from "../../insightPanel";
import AddIndicationPanel from "../../../pages/home/favoriteOrigin/AddIndicationPanel";
import OriginRow from "../multiLocQuotePanel/components/originRow";
import MobileSubFooter from "../multiLocQuotePanel/components/mobileSubFooter";
import FooterMultiLocation from "../multiLocQuotePanel/components/footerMultiLocation";
import SplitOriginRow, { SplitOriginRowHandle } from "./splitOriginRow";
import ProductNotAvailablePanel from "./productNotAvailablePanel";
import OccupantPickerModal from "../multiLocQuotePanel/components/OccupantPickerModal";
import { AddMissingProductModalState } from "../../../types/IMultiLocationQuoteMobile";
import {
  submitAddMissingProductForOrigin,
  handleAddMissingProductForOrigin as handleAddMissingProductForOriginUtil,
  getMonthForFavorite,
  getMonthKey,
  getMonthRange,
  normalizeFavoriteMonthPrices,
  normalizeFavoriteOriginPrices,
  sortByCreateDate,
} from "./mobileSplitFavoriteOriginUtils";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";

type OriginType = "koch" | "competitor";
type FilterType = "all" | "koch" | "competitor";

interface MobileFavoriteOriginsPanelProps {
  onSearchableChange?: (isSearchable: boolean) => void;
  onClose?: () => void;
  onCloseAll?: () => void;
  onShowTapHere?: (show: boolean) => void;
  onFooterSelect?: (tab: any) => void;
  selectedMonth?: string;
  setSelectedMonth?: (value: string) => void;
}

const mergePriceId = (
  price: IFavoriteOriginPeriodPrice | null,
  previousPrice: IFavoriteOriginPeriodPrice | null | undefined,
): IFavoriteOriginPeriodPrice | null =>
  price ? { ...price, id: previousPrice?.id } : price;

const mergeMonthIds = (
  previousMonth: IFavoriteOriginMonth | undefined,
  updatedMonth: IFavoriteOriginMonth,
): IFavoriteOriginMonth => ({
  ...updatedMonth,
  id: previousMonth?.id,
  periods: updatedMonth.periods.map((period, index) => {
    const previousPeriod = previousMonth?.periods?.[index];
    return {
      ...period,
      id: previousPeriod?.id,
      prices: {
        posted: mergePriceId(
          period.prices.posted,
          previousPeriod?.prices?.posted,
        ),
        truck: mergePriceId(period.prices.truck, previousPeriod?.prices?.truck),
        rail: mergePriceId(period.prices.rail, previousPeriod?.prices?.rail),
      },
    };
  }),
});

const MobileSplitFavoriteOriginsPanel: React.FC<
  MobileFavoriteOriginsPanelProps
> = ({
  onSearchableChange,
  onClose,
  onCloseAll,
  onShowTapHere,
  onFooterSelect,
  selectedMonth,
  setSelectedMonth,
}) => {
  const [
    globalProduct,
    priorityProducts,
    showGuidancePanel,
    setShowGuidancePanel,
    showIndicationHistoryPanel,
    setShowIndicationHistoryPanel,
    setSelectedFavoriteOrigin,
    refetchIndicationHistory,
    locations,
    setSelectedLocation,
    setTempPinLocation,
    openInsightsPanel,
    setShowAddIndicationHistoryPanel,
    showAddIndicationHistoryPanel,
    showInsightsPanel,
  ] = useNitroStore((state) => [
    state.product,
    state.priorityProducts,
    state.showGuidancePanel,
    state.setShowGuidancePanel,
    state.showIndicationHistoryPanel,
    state.setShowIndicationHistoryPanel,
    state.setSelectedFavoriteOrigin,
    state.refetchIndicationHistory,
    state.locations,
    state.setSelectedLocation,
    state.setTempPinLocation,
    state.openInsightsPanel,
    state.setShowAddIndicationHistoryPanel,
    state.showAddIndicationHistoryPanel,
    state.showInsightsPanel,
  ]);

  const [triggerRefetchMultiLocation, refetchMultiLocation] = useNitroCAStore(
    (state) => [state.triggerRefetchMultiLocation, state.refetchMultiLocation],
  );

  const [featureFlags, failedNotification] = useNitroStore((state) => [
    state.featureFlags,
    state.failedNotification,
  ]);
  const { originManager, multiRailStation } = featureFlags;

  const [setFavoriteOriginsByUser] = useNitroStore((state) => [
    state.setFavoriteOriginsByUser,
  ]);

  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;

  const selectedProduct = globalProduct || "AMMONIA";

  const [filterType, setFilterType] = useState<FilterType>("all");

  const [isSwitchingProduct, setIsSwitchingProduct] = useState(false);
  const [showProductNotAvailablePanel, setShowProductNotAvailablePanel] =
    useState(false);
  const [pendingOrigin, setPendingOrigin] = useState<{
    origin: SelectedOrigin;
    type: OriginType;
  } | null>(null);
  const [addMissingProductModalState, setAddMissingProductModalState] =
    useState<AddMissingProductModalState | null>(null);
  const [selectedOccupantKeys, setSelectedOccupantKeys] = useState<string[]>(
    [],
  );
  const [submittingAddMissingProduct, setSubmittingAddMissingProduct] =
    useState(false);
  const addMissingProductInFlightRef = useRef(false);
  const addMissingProductCooldownUntilRef = useRef(0);

  const [isAddingKoch, setIsAddingKoch] = useState(false);
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);
  const [currentAddingType, setCurrentAddingType] = useState<OriginType | null>(
    null,
  );
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  const [initialProduct, setInitialProduct] = useState(
    globalProduct || priorityProducts[0] || "AMMONIA",
  );
  const [initialKochCount, setInitialKochCount] = useState(0);
  const [initialCompetitorCount, setInitialCompetitorCount] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);

  const [pendingPriceChanges, setPendingPriceChanges] = useState<{
    [originId: string]: {
      [field in PriceField]?: string;
    };
  }>({});
  const debounceTimers = React.useRef<{ [originId: string]: NodeJS.Timeout }>(
    {},
  );

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const pendingRequestsRef = useRef<Set<Promise<unknown>>>(new Set());
  const [isLeavingPanel, setIsLeavingPanel] = useState(false);

  const trackBackgroundRequest = useCallback((request: Promise<unknown>) => {
    pendingRequestsRef.current.add(request);
    request.finally(() => {
      pendingRequestsRef.current.delete(request);
    });
  }, []);

  const awaitPendingRequestsBeforeNavigate = useCallback(async () => {
    if (pendingRequestsRef.current.size === 0) return;
    setIsLeavingPanel(true);
    try {
      await Promise.allSettled(Array.from(pendingRequestsRef.current));
    } finally {
      setIsLeavingPanel(false);
    }
  }, []);

  const [favoriteOriginsByProduct, setFavoriteOriginsByProduct] = useState<
    IFavoriteOriginPricesResponse[]
  >([]);
  const [outboundModesByOriginId, setOutboundModesByOriginId] = useState<
    Record<string, string[]>
  >({});
  const [isLoadingOutboundModes, setIsLoadingOutboundModes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentFetchRef = useRef<string | null>(null);
  const outboundModesFetchRef = useRef(0);
  const overlayRootRef = useRef<HTMLElement | null>(null);

  const fetchFavoriteOrigins = useCallback(
    async (forceRefresh = false, productOverride?: string) => {
      const productToUse = productOverride || selectedProduct;
      if (!user || !productToUse) {
        setFavoriteOriginsByProduct([]);
        setIsLoading(false);
        currentFetchRef.current = null;
        return;
      }
      const fetchKey = `${user}-${productToUse}`;

      if (!forceRefresh && currentFetchRef.current === fetchKey) {
        return;
      }

      setIsLoading(true);
      currentFetchRef.current = fetchKey;

      try {
        const normalized = await NitroService.getFavoritesByMarketer({
          marketer: user,
        });
        const raw = mapFavoritesByMarketerToLegacy(normalized);
        const result = normalizeFavoriteOriginPrices(raw);

        if (currentFetchRef.current === fetchKey) {
          setFavoriteOriginsByProduct(result);
        }
      } catch (error) {
        if (currentFetchRef.current === fetchKey) {
          setFavoriteOriginsByProduct([]);
        }
      } finally {
        if (currentFetchRef.current === fetchKey) {
          setIsLoading(false);
          currentFetchRef.current = null;
        }
      }
    },
    [user, selectedProduct],
  );

  useEffect(() => {
    fetchFavoriteOrigins();
  }, [fetchFavoriteOrigins]);

  const previousShowInsightsPanelRef = useRef(showInsightsPanel);
  useEffect(() => {
    if (previousShowInsightsPanelRef.current && !showInsightsPanel) {
      fetchFavoriteOrigins(true);
    }
    previousShowInsightsPanelRef.current = showInsightsPanel;
  }, [showInsightsPanel, fetchFavoriteOrigins]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    let root = document.getElementById("mobile-overlay-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "mobile-overlay-root";
      root.style.position = "fixed";
      root.style.top = "0";
      root.style.left = "0";
      root.style.right = "0";
      root.style.bottom = "0";
      root.style.zIndex = "100000";
      root.style.pointerEvents = "none";
      document.body.appendChild(root);
    }
    overlayRootRef.current = root as HTMLElement;
  }, []);

  const selectedMonthValue = useMemo(() => {
    if (selectedMonth && selectedMonth.length >= 7) {
      return selectedMonth.slice(0, 7);
    }
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  }, [selectedMonth]);

  const { kochOrigins, competitorOrigins } = useMemo(() => {
    const productOrigins =
      favoriteOriginsByProduct.find(
        (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
      )?.favorites || [];

    const koch: IFavoriteOriginWithMonths[] = [];
    const competitor: IFavoriteOriginWithMonths[] = [];

    productOrigins.forEach((origin) => {
      if (origin.competitor) {
        competitor.push(origin);
      } else {
        koch.push(origin);
      }
    });

    return {
      kochOrigins: koch.sort(sortByCreateDate),
      competitorOrigins: competitor.sort(sortByCreateDate),
    };
  }, [favoriteOriginsByProduct, selectedProduct]);

  const splitMonthKeys = useMemo(() => {
    const keys = new Set<string>();
    [...kochOrigins, ...competitorOrigins].forEach((fav) => {
      (fav.months || []).forEach((m) => {
        if (m.isSplit) {
          keys.add(getMonthKey(m.month));
        }
      });
    });
    return Array.from(keys);
  }, [kochOrigins, competitorOrigins]);

  useEffect(() => {
    if (favoriteOriginsByProduct.length === 0) return;
    setFavoriteOriginsByUser(favoriteOriginsByProduct);
  }, [favoriteOriginsByProduct, setFavoriteOriginsByUser]);

  const refreshOutboundModes = useCallback(async (locationIds: string[]) => {
    const fetchId = outboundModesFetchRef.current + 1;
    outboundModesFetchRef.current = fetchId;

    if (locationIds.length === 0) {
      setOutboundModesByOriginId({});
      setIsLoadingOutboundModes(false);
      return;
    }

    setIsLoadingOutboundModes(true);

    try {
      const useMultiRailStation =
        useNitroStore.getState().featureFlags.multiRailStation;
      const details =
        (useMultiRailStation
          ? await NitroService.getMultiLocationDetails2({ ids: locationIds })
          : await NitroService.getMultiLocationDetails({ ids: locationIds })) ||
        [];

      const nextOutboundModesByOriginId = (details as ILocation[]).reduce(
        (acc, location) => {
          if (!location?.id) return acc;
          acc[location.id] = (location.outboundModes || [])
            .map((mode) => String(mode || "").toUpperCase())
            .filter(Boolean);
          return acc;
        },
        {} as Record<string, string[]>,
      );

      setOutboundModesByOriginId(nextOutboundModesByOriginId);
    } catch (error) {
      console.error("Failed to refresh origin outbound modes", error);
    } finally {
      if (outboundModesFetchRef.current === fetchId) {
        setIsLoadingOutboundModes(false);
      }
    }
  }, []);

  const locationIdsKey = useMemo(() => {
    const ids = Array.from(
      new Set(
        [...kochOrigins, ...competitorOrigins]
          .map((origin) => origin.location?.id)
          .filter((id): id is string => Boolean(id)),
      ),
    ).sort();
    return ids.join(",");
  }, [kochOrigins, competitorOrigins]);

  useEffect(() => {
    const locationIds = locationIdsKey ? locationIdsKey.split(",") : [];
    refreshOutboundModes(locationIds);
  }, [locationIdsKey, multiRailStation, refetchMultiLocation, refreshOutboundModes]);

  const updateFavoriteOriginMonthLocally = useCallback(
    (locationId: string, updatedMonth: IFavoriteOriginMonth) => {
      setFavoriteOriginsByProduct((prev) =>
        prev.map((productEntry) => {
          if (
            productEntry.product.toUpperCase() !== selectedProduct.toUpperCase()
          ) {
            return productEntry;
          }
          return {
            ...productEntry,
            favorites: productEntry.favorites.map((fav) => {
              if (fav.location.id !== locationId) return fav;
              const monthKey = getMonthKey(updatedMonth.month);
              const existingMonths = fav.months || [];
              const idx = existingMonths.findIndex((m) => {
                return getMonthKey(m.month) === monthKey;
              });
              const newMonths = [...existingMonths];
              if (idx >= 0) {
                newMonths[idx] = updatedMonth;
              } else {
                newMonths.push(updatedMonth);
              }
              return { ...fav, months: newMonths };
            }),
          };
        }),
      );
    },
    [selectedProduct],
  );

  const favoriteOriginsByProductRef = useRef<IFavoriteOriginPricesResponse[]>(
    [],
  );
  useEffect(() => {
    favoriteOriginsByProductRef.current = favoriteOriginsByProduct;
  }, [favoriteOriginsByProduct]);

  const pendingPricePersistRef = useRef<
    Record<string, Promise<string | undefined>>
  >({});

  const originRowRefs = useRef<Map<string, SplitOriginRowHandle>>(new Map());
  const flushAllOriginRows = useCallback(() => {
    originRowRefs.current.forEach((instance) => instance.flush());
  }, []);

  const applyPeriodPriceId = useCallback(
    (
      locationId: string,
      competitor: boolean,
      monthKey: string,
      periodIndex: number,
      mode: "posted" | "truck" | "rail",
      periodPriceId: string,
    ) => {
      setFavoriteOriginsByProduct((prev) =>
        prev.map((productEntry) => {
          if (
            productEntry.product.toUpperCase() !== selectedProduct.toUpperCase()
          ) {
            return productEntry;
          }
          return {
            ...productEntry,
            favorites: productEntry.favorites.map((fav) => {
              if (
                fav.location.id !== locationId ||
                fav.competitor !== competitor
              ) {
                return fav;
              }
              return {
                ...fav,
                months: fav.months.map((m) => {
                  if (getMonthKey(m.month) !== monthKey) return m;
                  return {
                    ...m,
                    periods: m.periods.map((p, idx) => {
                      if (idx !== periodIndex) return p;
                      const currentPrice = p.prices[mode];
                      if (!currentPrice) return p;
                      return {
                        ...p,
                        prices: {
                          ...p.prices,
                          [mode]: { ...currentPrice, id: periodPriceId },
                        },
                      };
                    }),
                  };
                }),
              };
            }),
          };
        }),
      );
    },
    [selectedProduct],
  );

  const applySplitMonthResult = useCallback(
    (
      locationId: string,
      competitor: boolean,
      monthKey: string,
      result: IMonthSplitResponse,
    ) => {
      setFavoriteOriginsByProduct((prev) =>
        prev.map((productEntry) => {
          if (
            productEntry.product.toUpperCase() !== selectedProduct.toUpperCase()
          ) {
            return productEntry;
          }
          return {
            ...productEntry,
            favorites: productEntry.favorites.map((fav) => {
              if (
                fav.location.id !== locationId ||
                fav.competitor !== competitor
              ) {
                return fav;
              }
              return {
                ...fav,
                months: fav.months.map((m) => {
                  if (getMonthKey(m.month) !== monthKey) return m;
                  return {
                    ...m,
                    id: result.favoriteMonth.id,
                    isSplit: result.favoriteMonth.split,
                    periods: m.periods.map((p, idx) => {
                      if (idx !== 0) return p;
                      const prices = { ...p.prices };
                      result.periodPrices.forEach((price) => {
                        prices[price.name] = {
                          id: price.id,
                          price: price.price,
                          showAs: price.showAs,
                          requestedBy: price.updatedBy,
                          requestedAt: "",
                        };
                      });
                      return {
                        ...p,
                        id: result.period.id,
                        startDate: result.period.startDate,
                        endDate: result.period.endDate,
                        prices,
                      };
                    }),
                  };
                }),
              };
            }),
          };
        }),
      );
    },
    [selectedProduct],
  );

  const persistMonthChange = useCallback(
    (
      favorite: IFavoriteOriginWithMonths,
      updatedMonth: IFavoriteOriginMonth,
    ) => {
      if (!user || !selectedProduct) return;
      const monthKey = getMonthKey(updatedMonth.month);
      const previousMonth = (favorite.months || []).find(
        (m) => getMonthKey(m.month) === monthKey,
      );
      const locationId = favorite.location.id;
      const { competitor } = favorite;

      if (
        favorite.id &&
        previousMonth?.id &&
        previousMonth.isSplit !== updatedMonth.isSplit
      ) {
        const splitRequest = toggleSplitMonth(
          user,
          previousMonth,
          !updatedMonth.isSplit,
        )
          .then((response) => {
            const result = response?.[0];
            if (result) {
              applySplitMonthResult(locationId, competitor, monthKey, result);
            }
          })
          .catch((error) => {
            console.error("Failed to toggle split month:", error);
            failedNotification(
              "Failed to update split",
              "We could not save the split/unsplit change. Please try again.",
            );
          });
        trackBackgroundRequest(splitRequest);
      }

      if (!favorite.id) {
        return;
      }

      const modes: Array<"posted" | "truck" | "rail"> = [
        "posted",
        "truck",
        "rail",
      ];
      modes.forEach((mode) => {
        updatedMonth.periods.forEach((period, periodIndex) => {
          const previousPeriod = previousMonth?.periods?.[periodIndex];
          const newPrice = period.prices[mode];
          const oldPrice = previousPeriod?.prices?.[mode];
          const changed =
            (newPrice?.price ?? null) !== (oldPrice?.price ?? null) ||
            (newPrice?.showAs ?? "PRICE") !== (oldPrice?.showAs ?? "PRICE");
          if (!changed) return;

          const priceKey = `${locationId}|${competitor}|${monthKey}|${periodIndex}|${mode}`;
          const previousRequest =
            pendingPricePersistRef.current[priceKey] ||
            Promise.resolve(newPrice?.id);

          const priceRequest = previousRequest
            .catch(() => undefined)
            .then((resolvedId) => {
              const periodForRequest: IFavoriteOriginPeriod = resolvedId
                ? {
                    ...period,
                    prices: {
                      ...period.prices,
                      [mode]: { ...newPrice, id: resolvedId },
                    },
                  }
                : period;

              return persistPeriodPrice({
                marketer: user,
                product: selectedProduct,
                editedOrigin: favorite,
                monthEntry: updatedMonth,
                period: periodForRequest,
                mode,
              });
            })
            .then((created?: ICreatePeriodPriceResponse) => {
              if (created?.periodPriceId) {
                applyPeriodPriceId(
                  locationId,
                  competitor,
                  monthKey,
                  periodIndex,
                  mode,
                  created.periodPriceId,
                );
                return created.periodPriceId;
              }
              return newPrice?.id;
            })
            .catch((error) => {
              console.error(`Failed to save ${mode} price:`, error);
              failedNotification(
                "Failed to save price",
                `We could not save the ${mode} price change. Please try again.`,
              );
              return undefined;
            });

          pendingPricePersistRef.current[priceKey] = priceRequest;
          trackBackgroundRequest(priceRequest);
        });
      });
    },
    [
      user,
      selectedProduct,
      applySplitMonthResult,
      applyPeriodPriceId,
      trackBackgroundRequest,
      failedNotification,
    ],
  );

  const persistNewFavorite = useCallback(
    (origin: { id: string; name: string }, type: OriginType) => {
      if (!user || !selectedProduct) return;

      const productFavorites =
        favoriteOriginsByProductRef.current.find(
          (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
        )?.favorites || [];
      const order = productFavorites.filter(
        (fav) => fav.competitor === (type === "competitor"),
      ).length;

      const createRequest = createFavoriteCrud(
        user,
        selectedProduct,
        {
          location: { id: origin.id, name: origin.name, outboundModes: [] },
          competitor: type === "competitor",
          months: [],
        },
        order,
      )
        .then(() => fetchFavoriteOrigins(true))
        .catch((error) => {
          console.error("Failed to create favorite origin:", error);
          failedNotification(
            "Failed to add origin",
            "We could not save the new origin. Please try again.",
          );
        });

      trackBackgroundRequest(createRequest);
    },
    [
      user,
      selectedProduct,
      fetchFavoriteOrigins,
      trackBackgroundRequest,
      failedNotification,
    ],
  );

  useMutateFavoriteOrigins({
    user,
    product: selectedProduct,
  });
  const [isSearchable, setIsSearchable] = useState(false);

  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  useEffect(() => {
    if (isLoading && isSwitchingProduct) {
      setHasStartedLoading(true);
    }
  }, [isLoading, isSwitchingProduct]);

  useEffect(() => {
    if (!isLoading && isSwitchingProduct && hasStartedLoading) {
      setIsSwitchingProduct(false);
      setHasStartedLoading(false);
    }
  }, [isLoading, isSwitchingProduct, hasStartedLoading]);

  useEffect(() => {
    if (!isLoading && user && selectedProduct) {
      setInitialProduct(selectedProduct);
      setInitialKochCount(kochOrigins.length);
      setInitialCompetitorCount(competitorOrigins.length);
      setHasChanges(false);
    }
  }, [isLoading, user, selectedProduct, kochOrigins, competitorOrigins]);

  useEffect(() => {
    const productChanged = selectedProduct !== initialProduct;
    const kochCountChanged = kochOrigins.length !== initialKochCount;
    const competitorCountChanged =
      competitorOrigins.length !== initialCompetitorCount;

    setHasChanges(productChanged || kochCountChanged || competitorCountChanged);
  }, [
    selectedProduct,
    kochOrigins,
    competitorOrigins,
    initialProduct,
    initialKochCount,
    initialCompetitorCount,
  ]);

  useEffect(() => {
    const searchableValue = [...kochOrigins, ...competitorOrigins].length === 0;

    setIsSearchable(searchableValue);

    if (onSearchableChange) {
      onSearchableChange(searchableValue);
    }

    if (kochOrigins.length || competitorOrigins.length) {
      setPendingPriceChanges((prev) => {
        const updated = { ...prev };
        let hasPriceChanges = false;

        Object.entries(prev).forEach(([originId, pendingFields]) => {
          const allOrigins = [...kochOrigins, ...competitorOrigins];
          const backendOrigin = allOrigins.find(
            (o) => o.location.id === originId,
          ) as any;
          if (!backendOrigin) return;

          const fieldsToCheck = Object.entries(pendingFields);
          const allFieldsMatch = fieldsToCheck.every(
            ([field, pendingValue]) => {
              const backendValue =
                backendOrigin[`${field}Price` as keyof typeof backendOrigin];
              return String(backendValue ?? "") === pendingValue;
            },
          );

          if (allFieldsMatch && fieldsToCheck.length > 0) {
            delete updated[originId];
            hasPriceChanges = true;
          }
        });

        return hasPriceChanges ? updated : prev;
      });
    }
  }, [kochOrigins, competitorOrigins, selectedProduct, onSearchableChange]);

  const addOriginLocally = useCallback(
    (origin: { id: string; name: string }, type: OriginType) => {
      const month = selectedMonthValue;
      const { startDate, endDate } = getMonthRange(month);
      const parseDate = (
        dateStr: string,
      ): { year: number; month: number; day: number } => {
        const [y, mo, d] = dateStr.split("-").map(Number);
        return { year: y, month: mo, day: d };
      };
      const [yearStr, monthStr] = month.split("-");

      const newFavorite: IFavoriteOriginWithMonths = {
        location: { id: origin.id, name: origin.name, outboundModes: [] },
        competitor: type === "competitor",
        months: [
          {
            month: {
              year: Number(yearStr),
              month: Number(monthStr),
              day: 1,
            },
            isSplit: false,
            periods: [
              {
                startDate: parseDate(startDate),
                endDate: parseDate(endDate),
                prices: {
                  posted: { price: null, showAs: "PRICE" },
                  truck: { price: null, showAs: "PRICE" },
                  rail: { price: null, showAs: "PRICE" },
                },
              },
            ],
          },
        ],
      };

      const prev = favoriteOriginsByProductRef.current;
      const hasProduct = prev.some(
        (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
      );
      const updated = hasProduct
        ? prev.map((productEntry) => {
            if (
              productEntry.product.toUpperCase() !==
              selectedProduct.toUpperCase()
            )
              return productEntry;
            return {
              ...productEntry,
              favorites: [...productEntry.favorites, newFavorite],
            };
          })
        : [...prev, { product: selectedProduct, favorites: [newFavorite] }];

      favoriteOriginsByProductRef.current = updated;
      setFavoriteOriginsByProduct(updated);

      triggerRefetchMultiLocation();
    },
    [selectedMonthValue, selectedProduct, triggerRefetchMultiLocation],
  );

  const addOriginLocallyAndPersist = useCallback(
    (origin: { id: string; name: string }, type: OriginType) => {
      addOriginLocally(origin, type);
      persistNewFavorite(origin, type);
    },
    [addOriginLocally, persistNewFavorite],
  );

  const submitAddMissingProductForSelectedOccupants = () =>
    submitAddMissingProductForOrigin({
      addMissingProductModalState,
      selectedOccupantKeys,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      setSubmittingAddMissingProduct,
      setAddMissingProductModalState,
      setSelectedOccupantKeys,
      failedNotification,
      addOriginLocallyFn: addOriginLocallyAndPersist,
    });

  const handleAddMissingProductForOrigin = () =>
    handleAddMissingProductForOriginUtil({
      pendingOrigin,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      selectedProduct,
      setShowProductNotAvailablePanel,
      setAddMissingProductModalState,
      setSelectedOccupantKeys,
      failedNotification,
      addOriginLocallyFn: addOriginLocallyAndPersist,
    });

  const handleAddOrigin = (
    origin: SelectedOrigin | undefined,
    type: OriginType,
  ) => {
    if (!origin) {
      setCurrentAddingType(null);
      setIsAddingKoch(false);
      setIsAddingCompetitor(false);
      return;
    }

    if (
      originManager &&
      origin.products &&
      !origin.products
        .map((p) => p.toUpperCase())
        .includes(selectedProduct.toUpperCase())
    ) {
      setPendingOrigin({ origin, type });
      setShowProductNotAvailablePanel(true);
      setCurrentAddingType(null);
      setIsAddingKoch(false);
      setIsAddingCompetitor(false);
      return;
    }

    const locationRecord = locations.find(
      (location) => location.id === origin.id,
    );
    const typesToAdd: OriginType[] =
      locationRecord?.pinType === "kochAndCompetitor"
        ? ["koch", "competitor"]
        : [type];

    typesToAdd.forEach((originType) => {
      addOriginLocallyAndPersist(origin, originType);
    });

    setCurrentAddingType(null);
    setIsAddingKoch(false);
    setIsAddingCompetitor(false);
  };

  const handleCloseAddOriginPanel = () => {
    setCurrentAddingType(null);
    setIsAddingKoch(false);
    setIsAddingCompetitor(false);
  };

  const handleRemoveOrigin = (id: string, type: OriginType) => {
    const locationId = id.replace(`${type}-`, "");
    const isCompetitorRow = type === "competitor";

    const favoriteToRemove = [...kochOrigins, ...competitorOrigins].find(
      (fav) =>
        fav.location.id === locationId && fav.competitor === isCompetitorRow,
    );

    setFavoriteOriginsByProduct((prev) =>
      prev.map((productEntry) => ({
        ...productEntry,
        favorites: productEntry.favorites.filter(
          (fav) =>
            !(
              fav.location.id === locationId &&
              fav.competitor === isCompetitorRow
            ),
        ),
      })),
    );

    triggerRefetchMultiLocation();

    if (!user || !selectedProduct || !favoriteToRemove) return;

    const deleteRequest = deleteFavoriteCrud(
      user,
      selectedProduct,
      favoriteToRemove,
    ).catch((error) => {
      console.error("Failed to remove origin:", error);
      failedNotification(
        "Failed to remove origin",
        "We could not remove the origin. Please try again.",
      );
    });
    trackBackgroundRequest(deleteRequest);
  };

  const handleUpdateOrigin = (
    id: string,
    field: PriceField,
    value: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _type: OriginType,
  ) => {
    setPendingPriceChanges((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
    // favoriteOriginsByProduct is updated via onMonthChange in SplitOriginRow
  };

  const handleMenuAction = async (action: any, origin: OriginDisplayInfo) => {
    const locationId = origin.id.includes("-")
      ? origin.id.substring(origin.id.indexOf("-") + 1)
      : origin.id;
    const fullOrigin =
      action === "remove"
        ? null
        : [...kochOrigins, ...competitorOrigins].find(
            (o) => o.location.id === locationId,
          );
    if (fullOrigin) {
      setSelectedFavoriteOrigin([fullOrigin as unknown as IFavoriteOrigin]);
    }

    switch (action) {
      case "overview": {
        const selectedFavoriteOrigin = _.find(locations, (location) => {
          return location.id === locationId;
        });

        if (
          selectedFavoriteOrigin &&
          selectedFavoriteOrigin.pinType === "zipCodes"
        ) {
          return;
        }

        if (!selectedFavoriteOrigin) {
          console.warn("Overview target not found in locations", {
            locationId,
          });
          try {
            const details = await NitroService.getLocationDetails(locationId);
            const lat = Number(details?.coordinates?.[0]);
            const lng = Number(details?.coordinates?.[1]);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              setTempPinLocation({ lat, lng });
            }
          } catch (err) {
            console.error("Failed to load overview location details", err);
          }

          openInsightsPanel(locationId, "Logistics", false);
          break;
        }
        setSelectedLocation(selectedFavoriteOrigin);
        setTempPinLocation({
          lat: selectedFavoriteOrigin.lat,
          lng: selectedFavoriteOrigin.long,
        });

        openInsightsPanel(locationId, "Logistics", false);
        break;
      }
      case "guidance":
        setShowGuidancePanel(true);
        break;
      case "add-fob":
        setShowAddIndicationHistoryPanel(true);
        break;
      case "history":
        if (fullOrigin) {
          refetchIndicationHistory(
            fullOrigin.location.id,
            fullOrigin.location.name,
          );
        }
        setShowIndicationHistoryPanel(true);
        break;
      case "remove": {
        const originType = origin.id.split("-")[0] as OriginType;
        handleRemoveOrigin(origin.id, originType);
        break;
      }
      default:
        break;
    }
  };

  const renderOriginSection = (
    type: OriginType,
    origins: IFavoriteOriginWithMonths[],
    isAdding: boolean,
    setIsAdding: (adding: boolean) => void,
  ) => {
    const isKoch = type === "koch";
    const title = isKoch ? "Koch Origins" : "Competitor Origins";

    return (
      <section
        className="origins-section-mobile"
        id={`favorite-origins-section-id-${type}`}
      >
        {origins
          .filter((fav) => fav)
          .map((fav) => {
            const rowType: OriginType = fav.competitor ? "competitor" : "koch";
            const uniqueId = `${rowType}-${fav.location.id}`;
            const pending = pendingPriceChanges[uniqueId] || {};
            const outboundModes =
              outboundModesByOriginId[fav.location.id] ||
              (fav.location.outboundModes || [])
                .map((mode) => String(mode || "").toUpperCase())
                .filter(Boolean);
            const selectedFavoriteMonth =
              getMonthForFavorite(fav, selectedMonthValue) || undefined;

            const firstPeriod = selectedFavoriteMonth?.periods?.[0];
            const firstTruckPrice = firstPeriod?.prices?.truck?.price;
            const firstRailPrice = firstPeriod?.prices?.rail?.price;

            return originManager ? (
              <SplitOriginRow
                key={uniqueId}
                ref={(instance) => {
                  if (instance) {
                    originRowRefs.current.set(uniqueId, instance);
                  } else {
                    originRowRefs.current.delete(uniqueId);
                  }
                }}
                origin={{
                  id: uniqueId,
                  name: fav.location.name,
                  truckMin:
                    pending.truckMin ??
                    (firstTruckPrice === null || firstTruckPrice === undefined
                      ? ""
                      : String(firstTruckPrice)),
                  truckMax:
                    pending.truckMax ??
                    (firstTruckPrice === null || firstTruckPrice === undefined
                      ? ""
                      : String(firstTruckPrice)),
                  railMin:
                    pending.railMin ??
                    (firstRailPrice === null || firstRailPrice === undefined
                      ? ""
                      : String(firstRailPrice)),
                  railMax:
                    pending.railMax ??
                    (firstRailPrice === null || firstRailPrice === undefined
                      ? ""
                      : String(firstRailPrice)),
                  truckEnabled: String(true),
                  railEnabled: String(true),
                  bargeEnabled: String(false),
                }}
                onRemove={(id) => handleRemoveOrigin(id, rowType)}
                onUpdate={(id, field, value) =>
                  handleUpdateOrigin(id, field, value, rowType)
                }
                onAction={(actionType, originData) =>
                  handleMenuAction(actionType, originData)
                }
                activeMenuId={activeMenuId}
                setActiveMenuId={setActiveMenuId}
                type={rowType}
                onShowTapHere={onShowTapHere}
                outboundModes={outboundModes}
                selectedMonth={selectedFavoriteMonth}
                selectedMonthKey={selectedMonthValue}
                onMonthChange={(month) => {
                  const normalizedMonth = normalizeFavoriteMonthPrices(month);
                  const previousMonth = (fav.months || []).find(
                    (m) =>
                      getMonthKey(m.month) ===
                      getMonthKey(normalizedMonth.month),
                  );
                  const monthWithIds = mergeMonthIds(
                    previousMonth,
                    normalizedMonth,
                  );
                  updateFavoriteOriginMonthLocally(
                    fav.location.id,
                    monthWithIds,
                  );
                  persistMonthChange(fav, monthWithIds);
                }}
              />
            ) : (
              <OriginRow
                key={uniqueId}
                origin={{
                  id: uniqueId,
                  name: fav.location.name,
                  truckMin:
                    pending.truckMin ??
                    (firstTruckPrice === null || firstTruckPrice === undefined
                      ? ""
                      : String(firstTruckPrice)),
                  truckMax:
                    pending.truckMax ??
                    (firstTruckPrice === null || firstTruckPrice === undefined
                      ? ""
                      : String(firstTruckPrice)),
                  railMin:
                    pending.railMin ??
                    (firstRailPrice === null || firstRailPrice === undefined
                      ? ""
                      : String(firstRailPrice)),
                  railMax:
                    pending.railMax ??
                    (firstRailPrice === null || firstRailPrice === undefined
                      ? ""
                      : String(firstRailPrice)),
                  truckEnabled: String(true),
                  railEnabled: String(true),
                  bargeEnabled: String(false),
                }}
                onRemove={(id) => handleRemoveOrigin(id, rowType)}
                onUpdate={(id, field, value) =>
                  handleUpdateOrigin(id, field, value, rowType)
                }
                onAction={(actionType, originData) =>
                  handleMenuAction(actionType, originData)
                }
                activeMenuId={activeMenuId}
                setActiveMenuId={setActiveMenuId}
                type={rowType}
                onShowTapHere={onShowTapHere}
              />
            );
          })}

        {!isAdding && origins.length === 0 && (
          <div className="empty-state-container">
            <img
              data-tooltip-id="location-icon"
              src={icons.LocationIcon}
              alt="Location Icon"
              className="empty-state-icon"
            />
            <div className="empty-state-text">
              No {type} origins added yet
              {isKoch && <span style={{ color: "red" }}> (Required)</span>}
            </div>
            <div className="empty-state-subtext">
              Add origin and set FOB price
            </div>
          </div>
        )}

        {!isAdding && (
          <Button
            id={`add-origin-button-id-${type}`}
            className="add-origin-button"
            onClick={() => {
              setIsAdding(true);
              setCurrentAddingType(type);
              setIsAddingCompetitor(type === "competitor");
            }}
          >
            <span
              className="add-origin-text"
              style={{ color: title.includes("Koch") ? "#1D4ED8" : "#DC2626" }}
            >
              + Add {title.slice(0, -1)}
            </span>
          </Button>
        )}
      </section>
    );
  };

  const shouldHideSubFooter =
    showAddIndicationHistoryPanel || showIndicationHistoryPanel;

  return (
    <div
      id="mobile-indication-history-id-2"
      className={`${"mobile-favorite-origins-panel-container"}`}
    >
      <ProductNotAvailablePanel
        open={showProductNotAvailablePanel}
        productName={selectedProduct}
        onCancel={() => setShowProductNotAvailablePanel(false)}
        onAddProduct={handleAddMissingProductForOrigin}
      />

      <OccupantPickerModal
        modalState={addMissingProductModalState}
        selectedOccupantKeys={selectedOccupantKeys}
        submitting={submittingAddMissingProduct}
        onClose={() => setAddMissingProductModalState(null)}
        onToggleOccupant={(key) =>
          setSelectedOccupantKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
          )
        }
        onSubmit={submitAddMissingProductForSelectedOccupants}
      />

      {showGuidancePanel && (
        <GuidancePanel
          open={showGuidancePanel}
          close={() => setShowGuidancePanel(false)}
        />
      )}
      <InsightsPanel />

      <AddIndicationPanel />
      {showIndicationHistoryPanel && <IndicationHistoryPanel />}

      {currentAddingType && (
        <div className="add-origin-panel">
          <div className="add-origin-panel-content">
            <AddOriginColumn
              onAdd={(origin?: SelectedOrigin) =>
                handleAddOrigin(origin, currentAddingType)
              }
              onCancel={handleCloseAddOriginPanel}
              favoriteOrigins={
                currentAddingType === "koch"
                  ? (kochOrigins as unknown as IFavoriteOrigin[])
                  : (competitorOrigins as unknown as IFavoriteOrigin[])
              }
              context="mobileFavoriteOriginPanel"
              isCompetitor={isAddingCompetitor}
            />
          </div>
        </div>
      )}

      {isLeavingPanel &&
        (() => {
          const overlay = (
            <div
              className="adding-origin-overlay device-wide-overlay"
              style={{ pointerEvents: "auto" }}
            >
              <CircularProgress size="40px" className="adding-origin-spinner" />
              <div className="adding-origin-text">Saving changes...</div>
            </div>
          );
          return overlayRootRef.current
            ? createPortal(overlay, overlayRootRef.current)
            : overlay;
        })()}

      <div
        id="mobile-favorite-origins-content-split"
        className={`${"mobile-favorite-origins-content"}`}
      >
        <div
          id="origin-manager-title-id"
          className="favorite-origin-header-row"
        >
          <Button
            id="favorite-origin-close-back"
            aria-label="Close"
            onClick={async () => {
              flushAllOriginRows();
              triggerRefetchMultiLocation();

              if (onSearchableChange) {
                onSearchableChange(isSearchable);
              }

              await awaitPendingRequestsBeforeNavigate();

              if (onClose) {
                onClose();
              } else {
                useNitroStore
                  .getState()
                  .setShowMobileFavoriteOriginPanel(false);
              }

              if (hasChanges) {
                window.location.reload();
              }
            }}
          >
            <img
              data-tooltip-id="go-back-icon"
              src={icons.BackWithBackgroundIcon}
              alt="Go Back Icon"
              className="back-icon-favorite-origin"
            />
          </Button>
          <div
            id="favorite-origins-title-product"
            className="section-title-products"
          >
            Origins Manager
          </div>
        </div>

        <section className="products-section">
          <div
            id="origin-filter-toggle-container"
            className="origin-filter-toggle-container"
          >
            <Button
              className={`origin-filter-btn btn-all ${
                filterType === "all" ? "active" : ""
              }`}
              onClick={() => setFilterType("all")}
            >
              All
            </Button>
            <Button
              className={`origin-filter-btn btn-koch ${
                filterType === "koch" ? "active" : ""
              }`}
              onClick={() => setFilterType("koch")}
            >
              Koch
            </Button>
            <Button
              className={`origin-filter-btn btn-competitor ${
                filterType === "competitor" ? "active" : ""
              }`}
              onClick={() => setFilterType("competitor")}
            >
              Competitor
            </Button>
          </div>
        </section>

        {isLoading &&
          kochOrigins.length === 0 &&
          competitorOrigins.length === 0 &&
          (() => {
            const overlay = (
              <div
                className="adding-origin-overlay"
                style={{ pointerEvents: "auto" }}
              >
                <CircularProgress
                  size="40px"
                  className="adding-origin-spinner"
                />
                <div className="adding-origin-text">
                  Loading favorite origins...
                </div>
              </div>
            );
            return overlayRootRef.current
              ? createPortal(overlay, overlayRootRef.current)
              : overlay;
          })()}

        {isLoadingOutboundModes &&
          (() => {
            const overlay = (
              <div
                className="adding-origin-overlay"
                style={{ pointerEvents: "auto" }}
              >
                <CircularProgress
                  size="40px"
                  className="adding-origin-spinner"
                />
                <div className="adding-origin-text">
                  Loading origin modes...
                </div>
              </div>
            );
            return overlayRootRef.current
              ? createPortal(overlay, overlayRootRef.current)
              : overlay;
          })()}

        <div className="panel-scrollable-content">
          {isLoading &&
          kochOrigins.length === 0 &&
          competitorOrigins.length === 0 ? null : (
            <>
              {(filterType === "all" || filterType === "koch") &&
                renderOriginSection(
                  "koch",
                  kochOrigins,
                  isAddingKoch,
                  setIsAddingKoch,
                )}

              {(filterType === "all" || filterType === "competitor") &&
                renderOriginSection(
                  "competitor",
                  competitorOrigins,
                  isAddingCompetitor,
                  setIsAddingCompetitor,
                )}
            </>
          )}
        </div>

        <FooterMultiLocation
          selected="origins"
          onSelect={async (item) => {
            flushAllOriginRows();

            if (item === "account") {
              const state = useNitroStore.getState();
              state.setShowSettingsPanel?.(true);
              state.setShowMobileFavoriteOriginPanel?.(false);
              state.setShowMobileSearchPanel?.(false);
            }

            if (item === "quote" || item === "account") {
              await awaitPendingRequestsBeforeNavigate();
              if (onClose) {
                onClose();
              }
              if (onFooterSelect) {
                onFooterSelect(item);
              }
              return;
            }

            if (onFooterSelect) {
              onFooterSelect(item);
            }
          }}
        />
        {!shouldHideSubFooter && (
          <MobileSubFooter
            onClose={onClose}
            onCloseAll={onCloseAll}
            onMapClick={async () => {
              flushAllOriginRows();
              await awaitPendingRequestsBeforeNavigate();
            }}
            onProductSelectClick={flushAllOriginRows}
            onMonthSelectClick={flushAllOriginRows}
            forceMapButton
            selectedMonth={selectedMonth}
            setSelectedMonth={(value) => {
              if (setSelectedMonth) setSelectedMonth(value);
            }}
            onBeforeProductChange={awaitPendingRequestsBeforeNavigate}
            onBeforeListClick={awaitPendingRequestsBeforeNavigate}
            splitMonthKeys={splitMonthKeys}
          />
        )}
      </div>
    </div>
  );
};

export default MobileSplitFavoriteOriginsPanel;
